import { useNavigate } from 'react-router';

import Crown from '@/assets/icons/Crown';
import Button from '@/components/Buttons/Button';
import { biPlatformsGroup } from '@/features/DataHub/modules/Export/constants/biPlatforms';
import { optionById } from '@/features/DataHub/modules/Export/constants/exportOptions';
import {
  useExportMeta,
  useExportNameMeta,
  useExportPreview,
  useExportRequest
} from '@/features/DataHub/modules/Export/context/exportContexts';
import type { ButtonConfig } from '@/features/DataHub/types';
import { useAppDispatch } from '@/hooks/redux-hooks';
import useUserDetails from '@/hooks/useUserDetails';
import { closeDrawer } from '@/slices/drawerSlice';

const DataFooterButtons = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isUserPremium, isUserExpired } = useUserDetails();
  const { exportApiRequestString, exportCurlRequestString } = useExportMeta();
  const { exportName } = useExportNameMeta();
  const { previewOptionId, previewLoading, previewDownloadFn, previewContentToCopy } =
    useExportPreview();
  const {
    handleExportData,
    exportDataWithParams,
    handleCopyToClipBoard,
    capturePosthogEvent,
    loading,
    setExportStatus
  } = useExportRequest();

  const exportOption = previewOptionId ? (optionById.get(previewOptionId) ?? null) : null;

  const isIntegrationExport =
    exportOption?.preview?.type === 'integration' || exportOption?.preview?.type === 'sql';

  /** Python, Power Query, CSV, curl (json) etc. – content is in the preview; download = local file only, no API call */
  const isClientSideDownloadOnly =
    exportOption?.preview?.type === 'code' || exportOption?.preview?.type === 'simple';

  const shouldShowUpgradeButtons = !isUserPremium || isUserExpired;

  const buttons: ButtonConfig[] = shouldShowUpgradeButtons
    ? [{ label: 'Upgrade now', action: 'custom' }]
    : (exportOption?.buttons ?? []);

  const handleCopy = () => {
    const textToCopy = previewContentToCopy ?? exportCurlRequestString;
    handleCopyToClipBoard(textToCopy);
  };

  const handleDownloadOrExport = () => {
    capturePosthogEvent();

    // Integration exports (Power BI, Snowflake, etc.) → third-party API call
    if (isIntegrationExport && exportOption?.id) {
      setExportStatus('loading');
      exportDataWithParams({
        type: 'third-party',
        exportSubType: exportOption.id
      })
        .then(() => {
          setExportStatus('queued');
        })
        .catch(() => {
          setExportStatus('idle');
        });
      return;
    }

    // Client-side download exports (Python, CSV, curl, Power Query)
    if (isClientSideDownloadOnly && previewDownloadFn) {
      const isBIPlatformExport = biPlatformsGroup.options.some(o => o.id === exportOption?.id);
      if (isBIPlatformExport) {
        previewDownloadFn();
        return;
      }

      setExportStatus('loading');
      handleExportData()
        .then(() => {
          previewDownloadFn();
          setExportStatus('idle');
        })
        .catch(() => {
          setExportStatus('idle');
        });
      return;
    }

    // Server-side exports (Excel handles its own loading state)
    const isExcel = exportOption?.id === 'excel';
    if (!isExcel) {
      setExportStatus('loading');
    }

    handleExportData()
      .then(() => {
        previewDownloadFn?.();
        if (!isExcel) {
          setExportStatus('idle');
        }
      })
      .catch(() => {
        if (!isExcel) {
          setExportStatus('idle');
        }
      });
  };

  const handleLaunch = () => {
    capturePosthogEvent();
    setExportStatus('loading');
    handleExportData()
      .then(() => {
        if (exportApiRequestString) {
          window.open(exportApiRequestString, '_blank', 'noopener,noreferrer');
        }
      })
      .finally(() => {
        setExportStatus('idle');
      });
  };

  const handleUpgrade = () => {
    dispatch(closeDrawer());
    navigate('/manage-subscription');
  };

  const handleButtonClick = (button: ButtonConfig) => {
    if (button.action === 'copy') {
      handleCopy();
    } else if (button.action === 'download' || button.action === 'export') {
      handleDownloadOrExport();
    } else if (button.action === 'launch') {
      handleLaunch();
    } else if (button.label === 'Upgrade now') {
      handleUpgrade();
    } else if (button.onClick) {
      capturePosthogEvent();
      button.onClick();
    }
  };

  return (
    <>
      {buttons.length === 1 && (
        <Button
          sx={{
            color: 'primary.main',
            borderColor: 'primary.main'
          }}
          variant='outlined'
          onClick={() => {
            dispatch(closeDrawer());
          }}
        >
          Cancel
        </Button>
      )}
      {buttons.map(button => {
        const isExportAction =
          button.action === 'download' || button.action === 'export' || button.action === 'launch';
        const isNameInvalid = !exportName || exportName.trim() === '';
        const isClientSideDownload =
          (button.action === 'download' || button.action === 'export') && isClientSideDownloadOnly;
        const isDisabled =
          button.disabled ||
          (isExportAction &&
            (loading || previewLoading || (!isClientSideDownload && isNameInvalid)));

        return (
          <Button
            key={button.label}
            disabled={isDisabled}
            startIcon={button.label === 'Upgrade now' ? <Crown /> : undefined}
            variant={button.variant ?? 'contained'}
            onClick={() => {
              handleButtonClick(button);
            }}
          >
            {button.label}
          </Button>
        );
      })}
    </>
  );
};

export default DataFooterButtons;
